import React, { useState, useEffect } from 'react';
import { DB } from '../../data/database';
import { getWebsiteUnits, getContact, isSold, getUnitName, getPageContent } from './websiteStyles';
import AdvancedFilterModal from './AdvancedFilterModal';

/* ── design tokens ── */
const DARK = '#14161C';
const DARK2 = '#1E2028';
const PAPER = '#FAFAF8';
const PAPER2 = '#F3F3F1';
const INK = '#14161C';
const INK2 = '#52545C';
const INK3 = '#8B8D94';
const INK4 = '#B0B2B8';
const LINE = '#E5E5E3';
const BLUE = '#2563EB';
const GREEN = '#22C55E';
const mono = '"JetBrains Mono", monospace';
const heading = '"Inter Tight", sans-serif';
const serif = '"Instrument Serif", serif';

/* ─── car silhouette SVG ─── */
const CarSVG = ({ color = '#6B7280', bg = 'transparent', style }) => (
  <svg viewBox="0 0 800 320" fill="none" style={{ display: 'block', ...style }}>
    <ellipse cx="400" cy="275" rx="260" ry="10" fill="rgba(0,0,0,0.25)"/>
    <path d="M130 250 L130 215 Q130 210 135 210 L210 210 L250 150 Q255 142 265 140 L530 125 Q545 124 555 128 L640 160 Q648 163 655 168 L680 185 Q685 188 690 190 L700 192 Q710 194 710 204 L710 250 Q710 258 702 258 L138 258 Q130 258 130 250 Z" fill={color} opacity="0.85"/>
    <path d="M265 205 L290 152 Q293 147 300 146 L400 138 L400 205 Z" fill={bg || 'rgba(0,0,0,0.3)'}/>
    <path d="M410 205 L410 137 L520 130 Q528 130 534 136 L595 180 Q600 186 596 192 L596 205 Z" fill={bg || 'rgba(0,0,0,0.3)'}/>
    <path d="M160 258 Q160 222 200 222 Q240 222 240 258" fill={bg || 'rgba(0,0,0,0.4)'}/>
    <path d="M570 258 Q570 222 610 222 Q650 222 650 258" fill={bg || 'rgba(0,0,0,0.4)'}/>
    <circle cx="200" cy="258" r="32" fill={bg || '#181B22'} stroke={color} strokeWidth="3" opacity="0.9"/>
    <circle cx="200" cy="258" r="18" fill={color} opacity="0.3"/>
    <circle cx="200" cy="258" r="5" fill={color} opacity="0.6"/>
    <circle cx="610" cy="258" r="32" fill={bg || '#181B22'} stroke={color} strokeWidth="3" opacity="0.9"/>
    <circle cx="610" cy="258" r="18" fill={color} opacity="0.3"/>
    <circle cx="610" cy="258" r="5" fill={color} opacity="0.6"/>
    <rect x="695" y="195" width="10" height="16" rx="3" fill="#D1D5DB" opacity="0.5"/>
    <rect x="132" y="218" width="6" height="14" rx="2" fill="#EF4444" opacity="0.5"/>
  </svg>
);

/* ─── small category icons ─── */
const CatIcon = ({ type }) => {
  const s = { width: 28, height: 28, color: INK };
  const icons = {
    cuts: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><path d="M6 9a3 3 0 100-6 3 3 0 000 6zM6 21a3 3 0 100-6 3 3 0 000 6zM20 4L8.12 15.88M14.47 14.48L20 20M8.12 8.12L12 12"/></svg>,
    engine: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><circle cx="12" cy="12" r="3"/><path d="M12 1v4m0 14v4M4.22 4.22l2.83 2.83m9.9 9.9l2.83 2.83M1 12h4m14 0h4M4.22 19.78l2.83-2.83m9.9-9.9l2.83-2.83"/></svg>,
    drivetrain: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><circle cx="6" cy="6" r="3"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="18" r="3"/><path d="M6 9v9m12-9v9M9 6h6"/></svg>,
    suspension: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><path d="M12 2v2m0 16v2M6 8l1.5 2L6 12l1.5 2L6 16m12-8l-1.5 2L18 12l-1.5 2L18 16"/><circle cx="12" cy="12" r="3"/></svg>,
    body: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><rect x="3" y="11" width="18" height="7" rx="2"/><path d="M7 11V8a2 2 0 012-2h6a2 2 0 012 2v3"/><circle cx="7.5" cy="18" r="1.5"/><circle cx="16.5" cy="18" r="1.5"/></svg>,
    lights: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><circle cx="12" cy="12" r="5"/><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
    cooling: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/></svg>,
    interior: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><path d="M7 18v-6a5 5 0 0110 0v6"/><path d="M5 18h14v2a2 2 0 01-2 2H7a2 2 0 01-2-2v-2z"/><path d="M9 6V4a3 3 0 016 0v2"/></svg>,
  };
  return icons[type] || icons.engine;
};

/* ─── get hero background config ─── */
function getHeroConfig() {
  try {
    const cfg = DB.get('appConfig');
    if (cfg && typeof cfg === 'object' && !Array.isArray(cfg) && cfg.website_hero) {
      return cfg.website_hero;
    }
  } catch {}
  return { type: 'animation', image_url: '', video_url: '', overlay_opacity: 0.7 };
}

/* ─── get container count ─── */
function getContainerCount() {
  try {
    const containers = DB.get('containers') || [];
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    return containers.filter(c =>
      c.status === 'shipped' && (c.shipped_at || c.updatedAt || '') >= startOfMonth
    ).length || 0;
  } catch { return 0; }
}

/* ─── HomeUnitCard — matches "New arrivals" grid cards ─── */
function HomeUnitCard({ unit, contact, onClick }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
  const name = getUnitName(unit);
  const sold = isSold(unit);
  const img = unit.images?.[0];
  const featured = unit.is_featured;
  const reserved = unit.website_status === 'reserved';
  const arriving = unit.website_status === 'arriving';
  const km = unit.mileage ? (unit.mileage >= 1000 ? (unit.mileage / 1000).toFixed(1).replace('.0', '') + 'k' : unit.mileage) : null;
  const waMsg = encodeURIComponent(`Hi, I'm interested in ${name}${unit.sku ? ' ('+unit.sku+')' : ''}. Can you share details and pricing?`);

  return (
    <div onClick={onClick} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', border: `1px solid ${LINE}`, transition: 'box-shadow 0.25s, transform 0.25s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = ''; }}>
      <div style={{ position: 'relative', aspectRatio: '4/3', background: PAPER2, overflow: 'hidden' }}>
        {img ? (
          <img src={img} alt={name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', ...(sold ? { opacity: 0.5, filter: 'grayscale(40%)' } : {}) }} />
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 40px' }}>
            <CarSVG color="#B0B2B8" bg={PAPER2} style={{ width: '90%', opacity: 0.5 }} />
          </div>
        )}
        <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6 }}>
          {featured && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, background: '#DCFCE7', color: '#16A34A', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>● Featured</span>}
          {reserved && <span style={{ padding: '4px 10px', borderRadius: 6, background: '#FEF3C7', color: '#D97706', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Reserved</span>}
          {arriving && <span style={{ padding: '4px 10px', borderRadius: 6, background: '#DBEAFE', color: '#2563EB', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Arriving</span>}
          {sold && <span style={{ padding: '4px 10px', borderRadius: 6, background: '#FEE2E2', color: '#DC2626', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Sold</span>}
        </div>
        {unit.color && (
          <div style={{ position: 'absolute', top: 10, right: 10, padding: '4px 8px', borderRadius: 4, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', fontFamily: mono, fontSize: 9, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>
            {unit.color}
          </div>
        )}
      </div>
      <div style={{ padding: '14px 16px 16px' }}>
        <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.08em', color: INK3, textTransform: 'uppercase', marginBottom: 4 }}>
          {[unit.year, unit.make].filter(Boolean).join(' · ')}
        </div>
        <div style={{ fontFamily: heading, fontSize: 18, fontWeight: 600, color: INK, lineHeight: 1.2, marginBottom: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {unit.model || name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 16 }}>
            {km && <div><div style={{ fontFamily: mono, fontSize: 9, color: INK4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>KM</div><div style={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: INK2, marginTop: 2 }}>{km}</div></div>}
            {unit.fuel && <div><div style={{ fontFamily: mono, fontSize: 9, color: INK4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Fuel</div><div style={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: INK2, marginTop: 2 }}>{unit.fuel || unit.fuelType}</div></div>}
            {unit.transmission && <div><div style={{ fontFamily: mono, fontSize: 9, color: INK4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Trans</div><div style={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: INK2, marginTop: 2 }}>{unit.transmission}</div></div>}
          </div>
          {!sold && (
            <a href={`https://wa.me/${contact.whatsapp}?text=${waMsg}`} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 9999, background: GREEN, color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', opacity: 0.7 }}></span> Inquire
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  HERO FEATURED CARD with auto-rotate slideshow             */
/* ═══════════════════════════════════════════════════════════ */
function FeaturedSlideshow({ featured, onViewUnit }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
  const [activeIdx, setActiveIdx] = useState(0);

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (featured.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % featured.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featured.length]);

  if (featured.length === 0) return null;
  const unit = featured[activeIdx];
  if (!unit) return null;

  const km = unit.mileage ? (unit.mileage >= 1000 ? (unit.mileage / 1000).toFixed(1).replace('.0', '') + 'k' : unit.mileage) : null;

  return (
    <div style={{ background: DARK2, borderRadius: 16, border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden', cursor: 'pointer' }}
      onClick={() => onViewUnit(unit)}>
      {/* Card header: badge + carousel dots */}
      <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.35)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: BLUE }}></span>
          Featured unit · {activeIdx + 1} / {featured.length}
        </span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {featured.map((_, i) => (
            <span key={i} onClick={e => { e.stopPropagation(); setActiveIdx(i); }}
              style={{
                width: i === activeIdx ? 18 : 8, height: 4, borderRadius: 2,
                background: i === activeIdx ? BLUE : 'rgba(250,250,248,0.12)',
                transition: 'all 0.35s ease', cursor: 'pointer',
              }} />
          ))}
        </div>
      </div>

      {/* Color label */}
      {unit.color && (
        <div style={{ padding: '0 18px 6px', fontFamily: mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.2)' }}>
          ING · {unit.color}
        </div>
      )}

      {/* Car image area */}
      <div style={{ padding: '4px 24px', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {unit.images?.[0] ? (
          <img src={unit.images[0]} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8, transition: 'opacity 0.4s' }} />
        ) : (
          <CarSVG color="#7B8394" bg={DARK2} style={{ width: '85%' }} />
        )}
      </div>

      {/* VIN row */}
      <div style={{ padding: '0 18px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {unit.chassis ? (
          <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.08em', color: 'rgba(250,250,248,0.18)', textTransform: 'uppercase' }}>
            VIN · {unit.chassis.substring(0, 5)}...
          </div>
        ) : <div />}
        <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.08em', color: 'rgba(250,250,248,0.18)', textTransform: 'uppercase' }}>
          {unit.specs ? `${unit.specs} spec` : ''}{unit.specs && unit.year ? ' · ' : ''}{unit.year || ''}
        </div>
      </div>

      {/* Details section */}
      <div style={{ padding: '12px 18px 18px', borderTop: '1px solid rgba(255,255,255,0.04)', marginTop: 8 }}>
        {/* Year · Make row + SKU */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.08em', color: 'rgba(250,250,248,0.35)', textTransform: 'uppercase' }}>
            {[unit.year, unit.make].filter(Boolean).join(' · ')}
          </div>
          {unit.chassis && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.06em', color: 'rgba(250,250,248,0.25)', textTransform: 'uppercase' }}>Chassis</span>
              <span style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.04em', color: BLUE, fontWeight: 600 }}>{unit.chassis.substring(0, 5)}***</span>
            </div>
          )}
        </div>

        {/* Model name */}
        <div style={{ fontFamily: heading, fontSize: 28, fontWeight: 700, color: PAPER, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 16 }}>
          {unit.model || getUnitName(unit)}
        </div>

        {/* Specs grid */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 0, marginBottom: 16 }}>
          {[
            ['Year', unit.year || '—'],
            ['Color', unit.color || '—'],
            ['KM', km || '—'],
            ['Drive', unit.driveSystem || unit.drive || '—'],
          ].map(([label, val]) => (
            <div key={label} style={{ padding: '6px 0' }}>
              <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.25)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontFamily: mono, fontSize: 15, fontWeight: 600, color: PAPER }}>{val}</div>
            </div>
          ))}
        </div>

        {/* View button */}
        <button style={{ width: '100%', padding: '12px', borderRadius: 10, background: BLUE, color: '#fff', fontFamily: heading, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.2s' }}>
          View this unit <span>→</span>
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/*  HERO BACKGROUND — supports image, video, or animation    */
/* ═══════════════════════════════════════════════════════════ */
function HeroBackground({ config }) {
  const { type, image_url, video_url, overlay_opacity } = config;
  const overlayOp = overlay_opacity != null ? overlay_opacity : 0.7;

  if (type === 'image' && image_url) {
    return (
      <>
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <img src={image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: `rgba(20,22,28,${overlayOp})` }} />
      </>
    );
  }

  if (type === 'video' && video_url) {
    return (
      <>
        <video autoPlay muted loop playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
          <source src={video_url} type="video/mp4" />
        </video>
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: `rgba(20,22,28,${overlayOp})` }} />
      </>
    );
  }

  // Default: animation (grid pattern)
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 0, opacity: 0.04, pointerEvents: 'none', backgroundImage: `linear-gradient(rgba(250,250,248,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,248,0.5) 1px, transparent 1px)`, backgroundSize: '80px 80px' }} />
  );
}

/* AdvancedFilterModal imported from ./AdvancedFilterModal.jsx */

/* ═══════════════════════════════════════════════════════════ */
/*  HOMEPAGE                                                   */
/* ═══════════════════════════════════════════════════════════ */
function WebsiteHome({ onNavigate, onViewUnit }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
  const [searchMake, setSearchMake] = useState('');
  const [searchModel, setSearchModel] = useState('');
  const [searchYear, setSearchYear] = useState('');
  const [searchSteering, setSearchSteering] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const allUnits = getWebsiteUnits();
  const contact = getContact();
  const heroConfig = getHeroConfig();
  const containerCount = getContainerCount();
  const cms = getPageContent('website_homepage') || {};

  const vehicles = allUnits.filter(u => u._type === 'vehicle' && !u.cutType);
  const parts = allUnits.filter(u => u._type === 'part' || u._type === 'engine' || (u._type === 'vehicle' && u.cutType));
  const availableVehicles = vehicles.filter(u => !isSold(u));
  const featured = availableVehicles.filter(u => u.is_featured).slice(0, 5);
  const specialUnits = (featured.length > 0 ? featured : availableVehicles).slice(0, 4);
  const recentArrivals = [...availableVehicles].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).slice(0, 6);
  const totalAvailable = availableVehicles.length;

  const makes = [...new Set(vehicles.map(u => u.make).filter(Boolean))].sort();
  const models = searchMake ? [...new Set(vehicles.filter(u => u.make === searchMake).map(u => u.model).filter(Boolean))].sort() : [];
  const years = [...new Set(vehicles.map(u => u.year).filter(Boolean))].sort((a, b) => b - a);

  const catDefs = [
    { name: 'Cut Types', key: 'cut', icon: 'cuts' },
    { name: 'Engine & Trans', key: 'engine', icon: 'engine' },
    { name: 'Drivetrain', key: 'drivetrain|axle', icon: 'drivetrain' },
    { name: 'Suspension', key: 'suspension|steering|strut', icon: 'suspension' },
    { name: 'Body Panels', key: 'body panel|fender|bumper|bonnet|hood', icon: 'body' },
    { name: 'Lights', key: 'light|headl|tail l|fog', icon: 'lights' },
    { name: 'Cooling & AC', key: 'cool|radiator|condenser|a/c', icon: 'cooling' },
    { name: 'Interior', key: 'interior|seat|dashboard|console', icon: 'interior' },
  ];
  const partCats = catDefs.map(c => {
    const keys = c.key.split('|');
    const count = parts.filter(p => {
      const cat = (p.category || p.partName || p.engineName || p.cutType || '').toLowerCase();
      return keys.some(k => cat.includes(k));
    }).length;
    return { ...c, count };
  });

  const handleSearch = () => onNavigate('website-used-cars');

  const selectWrap = { flex: '1 1 140px', minWidth: 0 };
  const selectLabel = { fontFamily: mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.35)', marginBottom: 4 };
  const selectInput = { width: '100%', height: 40, padding: '0 12px', border: 'none', background: 'transparent', fontFamily: heading, fontSize: 14, color: PAPER, outline: 'none', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' };

  return (
    <div>
      {/* ═══════════ HERO (dark) ═══════════ */}
      <section style={{ background: DARK, color: PAPER, position: 'relative', overflow: 'hidden' }}>
        {/* Background: image / video / animation */}
        <HeroBackground config={heroConfig} />

        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 2 }}>
          {/* Top status bar */}
          {(cms.hero?.show_live_yard_badge !== false || cms.hero?.show_container_count !== false) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {cms.hero?.show_live_yard_badge !== false && (
            <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: GREEN }}></span>
              Live Yard · Sharjah · Updated 2 min ago
            </div>
            )}
            {cms.hero?.show_container_count !== false && (
            <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.3)' }} className="ws-hero-status-right">
              Containers shipped this month · <span style={{ color: 'rgba(250,250,248,0.6)' }}>{containerCount || 142}</span>
            </div>
            )}
          </div>
          )}

          {/* Hero content: left text + right card */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 440px', gap: isMobile ? 24 : 48, alignItems: 'center', padding: isMobile ? '40px 0 48px' : '72px 0 88px' }} className="ws-hero-grid">
            {/* Left */}
            <div>
              <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.35)', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: GREEN }}></span>
                {cms.hero?.eyebrow || 'Est. 2008 · Sharjah, UAE'}
              </div>
              {(() => {
                const hl = cms.hero?.headline || 'Pre-owned, certified, ready to drive.';
                const accent = cms.hero?.headline_accent_word || 'certified';
                const parts = hl.split(accent);
                return (
                  <h1 style={{ fontFamily: heading, fontWeight: 700, letterSpacing: '-0.04em', fontSize: 'clamp(44px, 6.5vw, 78px)', lineHeight: 0.95, marginBottom: 28 }}>
                    {parts[0]}
                    <span style={{ fontFamily: serif, fontStyle: 'italic', color: '#60A5FA', fontWeight: 400 }}>{accent}</span>
                    {parts[1] !== undefined ? parts[1].replace(/^,/, ',\n').split('\n').map((line, i) => i > 0 ? <><br />{line}</> : line) : ''}
                  </h1>
                );
              })()}
              <p style={{ fontSize: 16, color: 'rgba(250,250,248,0.45)', maxWidth: 480, lineHeight: 1.7, marginBottom: 36 }}>
                {cms.hero?.subheadline || 'Hand-picked used vehicles and genuine spare parts, exported worldwide, inquire by WhatsApp or call for a tailored quote, photos and shipping numbers the same day.'}
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={() => onNavigate('website-used-cars')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 28px', borderRadius: 9999, background: BLUE, color: '#fff', fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: heading }}>
                  {cms.hero?.primary_cta_text || 'Explore stock →'}
                </button>
                <button onClick={() => onNavigate('website-spare-parts')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 28px', borderRadius: 9999, background: 'transparent', color: PAPER, fontSize: 14, fontWeight: 500, border: '1px solid rgba(250,250,248,0.15)', cursor: 'pointer', fontFamily: heading }}>
                  {cms.hero?.secondary_cta_text || 'Spare parts catalog'}
                </button>
              </div>
            </div>

            {/* Right — featured unit slideshow */}
            <FeaturedSlideshow featured={featured.length > 0 ? featured : availableVehicles.slice(0, 3)} onViewUnit={onViewUnit} />
          </div>

          {/* ── Search bar (inside hero) ── */}
          <div style={{ paddingBottom: 32 }}>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', padding: '14px 20px', display: 'flex', alignItems: 'flex-end', gap: 0, flexWrap: 'wrap', backdropFilter: 'blur(8px)' }}>
              <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="rgba(250,250,248,0.3)" strokeWidth="2" style={{ width: 20, height: 20 }}><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              </div>
              <div style={{ ...selectWrap, borderRight: '1px solid rgba(255,255,255,0.08)', paddingRight: 16 }}>
                <div style={selectLabel}>Make</div>
                <select value={searchMake} onChange={e => { setSearchMake(e.target.value); setSearchModel(''); }} style={selectInput}>
                  <option value="">Any make</option>
                  {makes.map(m => <option key={m} value={m} style={{ color: INK, background: '#fff' }}>{m}</option>)}
                </select>
              </div>
              <div style={{ ...selectWrap, borderRight: '1px solid rgba(255,255,255,0.08)', padding: '0 16px' }}>
                <div style={selectLabel}>Model</div>
                <select value={searchModel} onChange={e => setSearchModel(e.target.value)} style={selectInput} disabled={!searchMake}>
                  <option value="">Any model</option>
                  {models.map(m => <option key={m} value={m} style={{ color: INK, background: '#fff' }}>{m}</option>)}
                </select>
              </div>
              <div style={{ ...selectWrap, borderRight: '1px solid rgba(255,255,255,0.08)', padding: '0 16px' }}>
                <div style={selectLabel}>Year</div>
                <select value={searchYear} onChange={e => setSearchYear(e.target.value)} style={selectInput}>
                  <option value="">Any year</option>
                  {years.map(y => <option key={y} value={y} style={{ color: INK, background: '#fff' }}>{y}</option>)}
                </select>
              </div>
              <div style={{ ...selectWrap, padding: '0 16px' }}>
                <div style={selectLabel}>Steering</div>
                <select value={searchSteering} onChange={e => setSearchSteering(e.target.value)} style={selectInput}>
                  <option value="" style={{ color: INK, background: '#fff' }}>Any steering</option>
                  <option value="LHD" style={{ color: INK, background: '#fff' }}>LHD</option>
                  <option value="RHD" style={{ color: INK, background: '#fff' }}>RHD</option>
                </select>
              </div>
              <button onClick={() => setAdvancedOpen(true)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', fontFamily: heading, fontSize: 13, fontWeight: 500, color: 'rgba(250,250,248,0.5)', cursor: 'pointer', marginLeft: 8 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M12 3v18M3 12h18"/></svg>
                Advanced
              </button>
              <button onClick={handleSearch} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '10px 24px', borderRadius: 10, background: BLUE, color: '#fff', fontFamily: heading, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', marginLeft: 8 }}>
                Search <span>→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ SPECIAL UNITS ═══════════ */}
      {specialUnits.length > 0 && cms.featured?.enabled !== false && (
        <section style={{ maxWidth: 1440, margin: '0 auto', padding: '80px 24px 60px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
            <div>
              <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: INK3, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: GREEN }}></span>
                {cms.featured?.eyebrow || 'Curated by our team · Live'}
              </div>
              <h2 style={{ fontFamily: heading, fontWeight: 700, letterSpacing: '-0.04em', fontSize: 'clamp(32px, 4vw, 48px)', lineHeight: 1, color: INK }}>
                {cms.featured?.heading || 'Special units'}
              </h2>
            </div>
            <button onClick={() => onNavigate('website-used-cars')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 9999, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 13, fontWeight: 500, color: INK, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {cms.featured?.view_all_text || 'View all stock →'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1fr', gap: 16, minHeight: isMobile ? 0 : 400 }} className="ws-special-grid">
            {specialUnits[0] && (
              <div onClick={() => onViewUnit(specialUnits[0])} style={{ background: DARK, borderRadius: 16, padding: 32, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', cursor: 'pointer', position: 'relative', overflow: 'hidden', gridRow: 'span 2' }}>
                {specialUnits[0].images?.[0] && (
                  <div style={{ position: 'absolute', inset: 0 }}>
                    <img src={specialUnits[0].images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.25 }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(20,22,28,0.95) 30%, rgba(20,22,28,0.4))' }} />
                  </div>
                )}
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
                    <span style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: GREEN, fontFamily: mono, fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>● Featured</span>
                    <span style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(250,250,248,0.06)', color: 'rgba(250,250,248,0.4)', fontFamily: mono, fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>This week</span>
                  </div>
                  <h3 style={{ fontFamily: heading, fontSize: 32, fontWeight: 700, color: PAPER, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 12 }}>
                    {getUnitName(specialUnits[0])}
                  </h3>
                  <p style={{ fontSize: 14, color: 'rgba(250,250,248,0.4)', lineHeight: 1.6, maxWidth: 400 }}>
                    {specialUnits[0].description || `${specialUnits[0].condition ? specialUnits[0].condition + ' condition' : 'Quality unit'}${specialUnits[0].specs ? ' · ' + specialUnits[0].specs + ' spec' : ''}. Available for immediate handover.`}
                  </p>
                </div>
              </div>
            )}

            {specialUnits.slice(1, 4).map(u => (
              <div key={u.id} onClick={() => onViewUnit(u)} style={{ background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, display: 'flex', gap: 16, padding: 16, cursor: 'pointer', alignItems: 'center', transition: 'box-shadow 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ width: 160, height: 100, borderRadius: 8, background: PAPER2, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {u.images?.[0] ? (
                    <img src={u.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <CarSVG color="#B0B2B8" bg={PAPER2} style={{ width: '80%', opacity: 0.5 }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {u.is_featured && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: mono, fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: GREEN, marginBottom: 6 }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: GREEN }}></span> Featured
                    </span>
                  )}
                  <div style={{ fontFamily: mono, fontSize: 10, color: INK3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>
                    {[u.year, u.make].filter(Boolean).join(' · ')}
                  </div>
                  <div style={{ fontFamily: heading, fontSize: 18, fontWeight: 600, color: INK, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.model || getUnitName(u)}
                  </div>
                  {u.sku && <div style={{ fontFamily: mono, fontSize: 10, color: INK4, letterSpacing: '0.06em' }}>{u.sku}</div>}
                </div>
                <span style={{ color: INK4, fontSize: 18 }}>›</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ═══════════ NEW ARRIVALS ═══════════ */}
      {recentArrivals.length > 0 && cms.recent?.enabled !== false && (
        <section style={{ background: PAPER2, borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}>
          <div style={{ maxWidth: 1440, margin: '0 auto', padding: '60px 24px 80px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: INK3, marginBottom: 8 }}>
                  {cms.recent?.eyebrow || 'Inventory · Updated 2 min ago'}
                </div>
                <h2 style={{ fontFamily: heading, fontWeight: 700, letterSpacing: '-0.04em', fontSize: 'clamp(28px, 4vw, 42px)', lineHeight: 1, color: INK }}>
                  {cms.recent?.heading || 'New arrivals'}
                </h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ fontFamily: mono, fontSize: 11, color: INK3 }}>
                  Showing {recentArrivals.length} of {totalAvailable}
                </span>
                <button onClick={() => onNavigate('website-used-cars')} style={{ padding: '8px 18px', borderRadius: 9999, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 13, fontWeight: 500, color: INK, cursor: 'pointer' }}>
                  All inventory
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {recentArrivals.map(u => (
                <HomeUnitCard key={u.id} unit={u} contact={contact} onClick={() => onViewUnit(u)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ SPARE PARTS (dark) ═══════════ */}
      {cms.parts?.enabled !== false && (
      <section style={{ background: DARK, color: PAPER, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, pointerEvents: 'none', backgroundImage: `linear-gradient(rgba(250,250,248,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,248,0.5) 1px, transparent 1px)`, backgroundSize: '80px 80px' }} />
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '80px 24px', position: 'relative' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 24 : 64, alignItems: 'center' }} className="ws-parts-grid">
            <div>
              <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.35)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: GREEN }}></span>
                {cms.parts?.eyebrow || 'Spare Parts'}
              </div>
              {(() => {
                const ptH = cms.parts?.heading || 'Genuine, used parts, in stock.';
                const ptA = cms.parts?.heading_accent_word || 'in stock.';
                const ptParts = ptH.split(ptA);
                return (
                  <h2 style={{ fontFamily: heading, fontWeight: 700, letterSpacing: '-0.04em', fontSize: 'clamp(36px, 5vw, 56px)', lineHeight: 1, marginBottom: 20 }}>
                    {ptParts[0]}
                    <span style={{ fontFamily: serif, fontStyle: 'italic', color: '#60A5FA', fontWeight: 400 }}>{ptA}</span>
                    {ptParts[1] || ''}
                  </h2>
                );
              })()}
              <p style={{ fontSize: 15, color: 'rgba(250,250,248,0.4)', lineHeight: 1.7, maxWidth: 420, marginBottom: 32 }}>
                {cms.parts?.subheading || 'Search by make, model & year — every part shipped from our warehouse, visit our yard today!'}
              </p>
              <button onClick={() => onNavigate('website-spare-parts')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 28px', borderRadius: 9999, background: GREEN, color: '#fff', fontFamily: heading, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                {cms.parts?.cta_text || 'Browse parts catalog →'}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 10 }}>
              {partCats.map(cat => (
                <div key={cat.name} onClick={() => onNavigate('website-spare-parts')} style={{ background: '#fff', borderRadius: 12, padding: '18px 14px', cursor: 'pointer', textAlign: 'center', transition: 'transform 0.2s, box-shadow 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: INK }}>
                    <CatIcon type={cat.icon} />
                  </div>
                  <div style={{ fontFamily: heading, fontSize: 12, fontWeight: 600, color: INK, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</div>
                  <div style={{ fontFamily: mono, fontSize: 10, color: INK3, letterSpacing: '0.06em' }}>{cat.count} skus</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ═══════════ VALUE PILLARS ═══════════ */}
      {cms.value_pillars?.enabled !== false && (
      <section style={{ borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '64px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 20 : 48 }} className="ws-pillars-grid">
            {(cms.value_pillars?.pillars || [
              { title: 'Hand-picked stock', description: 'Every unit walked through and selected by our buyers. Photos and condition notes shared on inquiry — honest descriptions, no surprises at port.' },
              { title: 'Export ready', description: 'GCC, Africa, CIS and Far-East export documentation handled in-house. Container loading and shipping consolidation from Jebel Ali.' },
              { title: 'Trade-friendly', description: 'Dealers, garages and exporters can register a trade account for bulk inquiries and dedicated pickup slots at the yard.' },
            ]).map((p, i) => {
              const icons = [
                <svg viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="1.5" style={{ width: 28, height: 28 }}><polyline points="20 6 9 17 4 12"/></svg>,
                <svg viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="1.5" style={{ width: 28, height: 28 }}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
                <svg viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="1.5" style={{ width: 28, height: 28 }}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
              ];
              return (
                <div key={p.title || i}>
                  <div style={{ marginBottom: 16 }}>{icons[i] || icons[0]}</div>
                  <div style={{ fontFamily: heading, fontSize: 20, fontWeight: 700, color: INK, marginBottom: 10, letterSpacing: '-0.02em' }}>{p.title}</div>
                  <div style={{ fontSize: 14, color: INK2, lineHeight: 1.7 }}>{p.description || p.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* ═══════════ Advanced Filter Modal ═══════════ */}
      <AdvancedFilterModal open={advancedOpen} onClose={() => setAdvancedOpen(false)} vehicles={vehicles} onNavigate={onNavigate} />

      {/* ═══════════ Responsive CSS ═══════════ */}
      <style>{`
        @media (max-width: 968px) {
          .ws-hero-grid { grid-template-columns: 1fr !important; }
          .ws-hero-status-right { display: none !important; }
          .ws-special-grid { grid-template-columns: 1fr !important; }
          .ws-parts-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .ws-pillars-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
        @media (max-width: 600px) {
          .ws-hero-grid h1 { font-size: 40px !important; }
        }
      `}</style>
    </div>
  );
}

export default WebsiteHome;
